import { Paper } from '@mui/material';
import {
  GridRenderCellParams,
  GridSortModel,
  GridValueFormatterParams,
} from '@mui/x-data-grid';
import Highcharts, { AxisLabelsFormatterContextObject } from 'highcharts';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useRecoilState, useRecoilValueLoadable } from 'recoil';

import { currentTeam } from '../../recoil/atoms/currentTeam';
import { endingDate } from '../../recoil/atoms/endingDate';
import { startingDate } from '../../recoil/atoms/startingDate';
import { IUserAtom, Role, user } from '../../recoil/atoms/user';
import { methodNameEnum } from '../../recoil/selectors/makePHPRequest';
import {
  IPermissions,
  permissionsSelector,
} from '../../recoil/selectors/permissionsSelector';
import { timeSaved } from '../../recoil/selectors/timeSaved';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import { hhmmss } from '../components/CallTranscriptions/CallInfo';
import { HighchartsReactWithStyles } from '../components/charts/HighchartsReactWithStyles';
import AgentMetricDropdown from '../components/dropdowns/AgentMetricDropdown';
import { TeamDropdown } from '../components/dropdowns/TeamDropdown';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import {
  DataTable,
  IColDef,
} from '../modules/Manage/pages/queues-table/DataTable';
import { EnhancedTableToolbar } from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';
import { QueuesSkeleton } from '../modules/Skeleton/QueuesSkeleton';

export interface IAgentStatusMetric {
  acwAvgTime: number;
  acwTotalTime: number;
  avgCallHandleTime: number;
  callHandleAvgTime: number;
  callHandleTotalTime: number;
  callsHandled: number;
  dndTime: number;
  email: string;
  id: string;
  idleTime: number;
  loggedInTime: number;
  speedToAnswerAvgTime: number;
  speedToAnswerTotalTime: number;
  totalCallHandleTime: number;
  userid: string;
}

export interface IAgentBarMetric {
  count: number[];
  emails: string[];
}

export interface IMetric {
  title: string;
  key: string;
}

export const allMetrics = {
  loggedInTime: {
    title: 'Logged In',
    key: 'computedLoggedInTime',
  },
  idleTime: {
    title: 'Idle',
    key: 'idleTime',
  },
  dndTime: {
    title: 'DND',
    key: 'dndTime',
  },
  callsHandled: {
    title: 'Calls Handled',
    key: 'callsHandled',
  },
  totalCallHandleTime: {
    title: 'Total Call Handle Time',
    key: 'totalCallHandleTime',
  },
  avgCallHandleTime: {
    title: 'Avg Call Handle Time',
    key: 'avgCallHandleTime',
  },
  speedToAnswerTotalTime: {
    title: 'Total Speed To Answer',
    key: 'speedToAnswerTotalTime',
  },
  speedToAnswerAvgTime: {
    title: 'Avg Speed To Answer',
    key: 'speedToAnswerAvgTime',
  },
  callHandleTotalTime: {
    title: 'Total Call Length',
    key: 'callHandleTotalTime',
  },
  callHandleAvgTime: {
    title: 'Avg Call Length',
    key: 'callHandleAvgTime',
  },
  acwTotalTime: {
    title: 'Total After Call Work',
    key: 'acwTotalTime',
  },
  acwAvgTime: {
    title: 'Avg After Call Work',
    key: 'acwAvgTime',
  },
};

const AgentStatus = () => {
  const [team, _setTeam] = useRecoilState(currentTeam);

  const timeSavedData = useRecoilValueLoadable(timeSaved);
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const { role } = permissionsLoadable.contents as IPermissions;
  const isUser = role === Role.USER;
  const userAtomLoadable = useRecoilValueLoadable(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  const [metric, setMetric] = useState({
    ...allMetrics.totalCallHandleTime,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'callsHandled', sort: 'desc' },
  ]);
  const startDate = useRecoilValueLoadable(startingDate);
  const endDate = useRecoilValueLoadable(endingDate);
  const start = startDate.state === 'hasValue' ? startDate.contents : undefined;
  const end = endDate.state === 'hasValue' ? endDate.contents : undefined;
  const callData = {
    call: methodNameEnum.GET_BUSINESS_USERS,
  };
  const users = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify(callData),
    })
  );

  const activeAgents = useMemo(
    () =>
      users?.contents?.response?.data?.result || [].length > 0
        ? [...(users?.contents?.response?.data?.result ?? [])]
            ?.filter((user) => user.isactive === 1)
            ?.sort((a, b) =>
              a.fname.toLowerCase().localeCompare(b.fname.toLowerCase())
            )
            ?.reduce(
              (acc, item) => ({
                ...acc,
                ids: acc.ids.concat(item.userid),
                lookup: {
                  ...acc.lookup,
                  [item.userid]: item,
                },
              }),
              {
                ids: [],
                lookup: {},
              }
            )
        : [],
    [users]
  );

  const metricOrMaxValue = (
    metric: number | null,
    maxMetricTimeSpan: number
  ) => {
    return !metric || isNaN(maxMetricTimeSpan) || metric < maxMetricTimeSpan
      ? metric
      : maxMetricTimeSpan;
  };

  const maxMetricTimeSpan = useMemo(() => {
    if (!start || !end) return undefined;
    try {
      return (
        new Date((end as moment.Moment).toDate()).getTime() -
        new Date((start as moment.Moment).toDate()).getTime()
      );
    } catch (e) {
      return undefined;
    }
  }, [start, end]);

  const [
    _,
    agentStatusMetricsLoadable,
    setRefetchToggleForAgentStatusMetrics,
  ] = makeApiRequestCached(
    {
      method: 'post',
      stage: 'itn',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      endpoint: endpointEnum.GET_AGENT_STATUS_METRICS,
      payloadJsonString: JSON.stringify({
        apikey: userAtomContents?.apikey,
        startDate: startDate.contents,
        endDate: endDate.contents,
        type: team?.id !== 0 ? 'team' : 'business',
        id: team?.id ?? 0,
        businessid: userAtomContents?.businessid,
        source: 'call-metrics',
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data.charts ?? cached.current
  );

  useEffect(() => {
    setRefetchToggleForAgentStatusMetrics({});
  }, [team, setRefetchToggleForAgentStatusMetrics]);

  const columns = useMemo<IColDef[]>(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        hide: true,
        disableSearch: true,
        enableSorting: false,
      },
      {
        field: 'email',
        headerName: 'Email',
        width: 200,
        enableSorting: true,
        renderCell: (params) => {
          return params.value === 'N/A' || isUser ? (
            <>{params.value}</>
          ) : (
            <NavLink to={`/manage/agents/${params.row.userid}/edit`}>
              {params.value}
            </NavLink>
          );
        },
      },
      {
        field: 'computedLoggedInTime',
        headerName: allMetrics.loggedInTime.title,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: true,
        valueFormatter: (params: GridValueFormatterParams) =>
          hhmmss((params.value as number) ?? 0),
        renderCell: (params: GridRenderCellParams<unknown>) => {
          const time = params.row.computedLoggedInTime;
          return hhmmss(
            maxMetricTimeSpan && time
              ? metricOrMaxValue(time, maxMetricTimeSpan) ?? 0
              : time ?? 0
          );
        },
      },
      {
        field: allMetrics.idleTime.key,
        headerName: allMetrics.idleTime.title,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: true,
        valueFormatter: (params: GridValueFormatterParams) =>
          hhmmss((params.value as number) ?? 0),
        renderCell: (params: GridRenderCellParams<unknown>) => {
          return hhmmss((params.value as number) ?? 0);
        },
      },
      {
        field: allMetrics.dndTime.key,
        headerName: allMetrics.dndTime.title,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: true,
        valueFormatter: (params: GridValueFormatterParams) =>
          hhmmss((params.value as number) ?? 0),
        renderCell: (params: GridRenderCellParams<unknown>) => {
          return hhmmss((params.value as number) ?? 0);
        },
      },
      {
        field: allMetrics.callsHandled.key,
        headerName: allMetrics.callsHandled.title,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        valueFormatter: (params: GridValueFormatterParams) =>
          (params.value as number) ?? 0,
        enableSorting: true,
      },
      {
        field: allMetrics.totalCallHandleTime.key,
        headerName: allMetrics.totalCallHandleTime.title,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: true,
        valueFormatter: (params: GridValueFormatterParams) =>
          hhmmss((params.value as number) ?? 0),
        renderCell: (params: GridRenderCellParams<unknown>) => {
          return hhmmss((params.value as number) ?? 0);
        },
      },
      {
        field: allMetrics.avgCallHandleTime.key,
        headerName: allMetrics.avgCallHandleTime.title,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: true,
        valueFormatter: (params: GridValueFormatterParams) =>
          hhmmss((params.value as number) ?? 0),
        renderCell: (params: GridRenderCellParams<unknown>) => {
          return hhmmss((params.value as number) ?? 0);
        },
      },
      {
        field: allMetrics.speedToAnswerTotalTime.key,
        headerName: allMetrics.speedToAnswerTotalTime.title,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: true,
        valueFormatter: (params: GridValueFormatterParams) =>
          hhmmss((params.value as number) ?? 0),
        renderCell: (params: GridRenderCellParams<unknown>) => {
          return hhmmss((params.value as number) ?? 0);
        },
      },
      {
        field: allMetrics.speedToAnswerAvgTime.key,
        headerName: allMetrics.speedToAnswerAvgTime.title,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: true,
        valueFormatter: (params: GridValueFormatterParams) =>
          hhmmss((params.value as number) ?? 0),
        renderCell: (params: GridRenderCellParams<unknown>) => {
          return hhmmss((params.value as number) ?? 0);
        },
      },
      {
        field: allMetrics.callHandleTotalTime.key,
        headerName: allMetrics.callHandleTotalTime.title,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: true,
        valueFormatter: (params: GridValueFormatterParams) =>
          hhmmss((params.value as number) ?? 0),
        renderCell: (params: GridRenderCellParams<unknown>) => {
          return hhmmss((params.value as number) ?? 0);
        },
      },
      {
        field: allMetrics.callHandleAvgTime.key,
        headerName: allMetrics.callHandleAvgTime.title,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: true,
        valueFormatter: (params: GridValueFormatterParams) =>
          hhmmss((params.value as number) ?? 0),
        renderCell: (params: GridRenderCellParams<unknown>) => {
          return hhmmss((params.value as number) ?? 0);
        },
      },
      {
        field: allMetrics.acwTotalTime.key,
        headerName: allMetrics.acwTotalTime.title,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: true,
        valueFormatter: (params: GridValueFormatterParams) =>
          hhmmss((params.value as number) ?? 0),
        renderCell: (params: GridRenderCellParams<unknown>) => {
          return hhmmss((params.value as number) ?? 0);
        },
      },
      {
        field: allMetrics.acwAvgTime.key,
        headerName: allMetrics.acwAvgTime.title,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: true,
        valueFormatter: (params: GridValueFormatterParams) =>
          hhmmss((params.value as number) ?? 0),
        renderCell: (params: GridRenderCellParams<unknown>) => {
          return hhmmss((params.value as number) ?? 0);
        },
      },
    ],
    [isUser, maxMetricTimeSpan]
  );

  const rows = useMemo<IAgentStatusMetric[]>(() => {
    if (
      agentStatusMetricsLoadable?.state === 'hasValue' &&
      agentStatusMetricsLoadable?.contents &&
      !('exception' in (agentStatusMetricsLoadable?.contents ?? [])) &&
      activeAgents?.ids?.length > 0
    ) {
      return agentStatusMetricsLoadable.contents.response.data.agents.map(
        (agent) => {
          const totalCallHandleTime =
            (agent?.totalCallHandleTime ?? 0) +
            (agent?.speedToAnswerTotalTime ?? 0);
          const avgCallHandleTime =
            (agent?.avgCallHandleTime ?? 0) +
            (agent?.speedToAnswerAvgTime ?? 0);

          const idleTime = agent?.idleTime ?? 0;
          const dndTime = agent?.dndTime ?? 0;

          const computedLoggedInTime = Math.min(
            maxMetricTimeSpan ?? Infinity,
            Math.max(
              agent?.loggedInTime ?? 0,
              totalCallHandleTime + idleTime + dndTime
            )
          );

          return {
            ...agent,
            id: agent?.userid,
            email: activeAgents?.lookup?.[agent?.userid]?.email,
            totalCallHandleTime,
            avgCallHandleTime,
            computedLoggedInTime,
          };
        }
      );
    } else {
      return [];
    }
  }, [agentStatusMetricsLoadable, activeAgents, maxMetricTimeSpan]);

  const ToolbarComponent = useCallback(
    (props) => <EnhancedTableToolbar scaleLoadingForSearch={true} {...props} />,
    []
  );
  const barMetrics = useMemo<IAgentBarMetric>(() => {
    if (rows.length && agentStatusMetricsLoadable?.state === 'hasValue') {
      const mKey = metric.key;
      const sortedAgents: IAgentStatusMetric[] = rows.sort((a, b) =>
        a[mKey] < b[mKey] ? 1 : a[mKey] > b[mKey] ? -1 : 0
      );
      return (
        sortedAgents.reduce(
          (acc: IAgentBarMetric, item: IAgentStatusMetric) => {
            return {
              ...acc,
              count:
                item?.[mKey] && item?.[mKey] !== 0
                  ? [...acc.count, item?.[mKey] ?? 0]
                  : [...acc.count],
              emails:
                item?.[mKey] && item?.[mKey] !== 0
                  ? [...acc.emails, activeAgents?.lookup?.[item?.userid]?.email]
                  : [...acc.emails],
            };
          },
          {
            count: [],
            emails: [],
          } as IAgentBarMetric
        ) ?? ({ count: [], emails: [] } as IAgentBarMetric)
      );
    } else {
      return { count: [], emails: [] };
    }
  }, [activeAgents?.lookup, agentStatusMetricsLoadable?.state, metric, rows]);

  const chartOptions = useMemo(() => {
    return {
      chart: {
        height: 600,
        type: 'bar',
      },
      title: {
        text: 'Agent Status',
      },
      xAxis: {
        categories: barMetrics.emails,
      },
      yAxis: {
        title: {
          text: `${metric.title}`,
        },
        labels: {
          formatter: function (this: AxisLabelsFormatterContextObject) {
            return metric.key == 'callsHandled'
              ? this.value
              : hhmmss(Number(this.value));
          },
        },
      },
      tooltip: {
        formatter: function () {
          return (
            this.x +
            '<br/>' +
            metric.title +
            ': <b>' +
            (metric.key == 'callsHandled' ? this.y : hhmmss(Number(this.y))) +
            '</b>'
          );
        },
        shared: true,
      },
      credits: {
        enabled: false,
      },
      series: [
        {
          data: barMetrics.count,
          name: '',
          showInLegend: false,
          type: (undefined as unknown) as 'bar', // @todo fix up types
        },
      ],
    } as Highcharts.Options;
  }, [metric, barMetrics]);

  return (
    <Paper square={true} elevation={0}>
      <TeamDropdown />
      <AgentMetricDropdown onSetMetric={setMetric} metric={metric} />
      <div>
        <HighchartsReactWithStyles
          allowChartUpdate={true}
          highcharts={Highcharts}
          options={chartOptions}
          loading={agentStatusMetricsLoadable?.state !== 'hasValue'}
        />
      </div>
      {timeSavedData?.state === 'loading' && <QueuesSkeleton />}
      <DataTable
        rows={rows}
        columns={columns}
        sortModel={sortModel}
        setGridSortModel={setSortModel}
        ToolbarComponent={ToolbarComponent}
      />
    </Paper>
  );
};

export default AgentStatus;
